import { DestroyRef } from '@angular/core';
import {
  AlertVariant,
  NotificationService,
  NotificationType,
} from '@kaspacom/ui-kit';

/**
 * Mirrors a reactive `{variant, text}[]` condition (a computed status, or an
 * externally-owned signal read via `effect()`) into kc-snackbar toasts,
 * diffing against the previous value per `key` so an unchanged value is a
 * no-op rather than a re-flash. Not for one-shot action results — those
 * should call `NotificationService` directly at the point of success/failure.
 */
export class NotificationGroupSync {
  private readonly groups = new Map<
    string,
    { serialized: string; ids: string[] }
  >();

  constructor(
    private readonly notificationService: NotificationService,
    destroyRef: DestroyRef,
    private readonly durationMs = 8000,
  ) {
    destroyRef.onDestroy(() => this.clear());
  }

  sync(
    key: string,
    title: string,
    items: readonly { variant: AlertVariant; text: string }[],
  ): void {
    const serialized = JSON.stringify({ title, items });
    const previous = this.groups.get(key);
    if (previous?.serialized === serialized) {
      return;
    }
    previous?.ids.forEach((id) => this.notificationService.removeMessage(id));
    if (items.length === 0) {
      this.groups.delete(key);
      return;
    }
    const ids = items.map((item) =>
      this.notificationService.pushMessage({
        type: this.typeForVariant(item.variant),
        title,
        description: item.text,
        duration: this.durationMs,
      }),
    );
    this.groups.set(key, { serialized, ids });
  }

  clear(): void {
    this.groups.forEach((group) =>
      group.ids.forEach((id) => this.notificationService.removeMessage(id)),
    );
    this.groups.clear();
  }

  private typeForVariant(variant: AlertVariant): NotificationType {
    switch (variant) {
      case 'error':
        return NotificationType.Error;
      case 'success':
        return NotificationType.Success;
      case 'warning':
        return NotificationType.Warning;
      case 'info':
      default:
        return NotificationType.Processing;
    }
  }
}
